import PendoCard from '../../components/card/pendo-card.vue.js';
import PendoEditableContent from '../../components/editable-content/pendo-editable-content.vue.js';
import PendoButton from '../../components/button/pendo-button.vue.js';
import EditExtensionDomainPatterns from './app-details-subcomponents/edit-extension-domain-patterns.vue.js';
import DisplayEditExtensionRegularExpressions from './app-details-subcomponents/display-edit-extension-regular-expressions.vue.js';
import PendoCheckbox from '../../components/checkbox/pendo-checkbox.vue.js';
import PendoIcon from '../../components/icon/pendo-icon.vue.js';
import PendoTooltip from '../../directives/tooltip/pendo-tooltip.js';
import PendoDataCollectionConsentToggle from '../data-collection-consent-toggle/pendo-data-collection-consent-toggle.vue.js';
import AppColorPicker from './app-details-subcomponents/app-color-picker.vue.js';
import PendoFaviconUpload from '../favicon-upload/pendo-favicon-upload.vue.js';
import { validateCookieDomain, validateCookieTTL } from './utils.js';
import get from 'lodash/get.js';
import uniq from 'lodash/uniq.js';
import upperFirst from 'lodash/upperFirst.js';
import Vibrant from 'node-vibrant';

function _empty() {}
function _awaitIgnored(value, direct) {
  if (!direct) {
    return value && value.then ? value.then(_empty) : Promise.resolve();
  }
}
function _invokeIgnored(body) {
  var result = body();
  if (result && result.then) {
    return result.then(_empty);
  }
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoAppDetails',
  components: {
    PendoCard,
    PendoEditableContent,
    PendoButton,
    PendoCheckbox,
    PendoIcon,
    PendoDataCollectionConsentToggle,
    PendoFaviconUpload,
    AppColorPicker,
    EditExtensionDomainPatterns,
    DisplayEditExtensionRegularExpressions
  },
  directives: {
    PendoTooltip
  },
  props: {
    app: {
      type: Object,
      required: true
    },
    faviconUrl: {
      type: Object,
      default: () => {}
    },
    subscription: {
      type: Object,
      required: true
    },
    updatingAppFlag: {
      type: String,
      required: true
    },
    updatingApp: {
      type: Boolean,
      required: true
    },
    submissionError: {
      type: Object,
      default: () => {}
    },
    savingChanges: {
      type: Boolean,
      default: false
    },
    isSuperUser: {
      type: Boolean,
      default: false
    },
    isOpsUser: {
      type: Boolean,
      default: false
    },
    consumingPlatform: {
      type: String,
      required: true
    },
    engageCoreEnabled: {
      type: Boolean,
      default: false
    },
    engageFeedbackEnabled: {
      type: Boolean,
      default: false
    },
    isEngage: {
      type: Boolean,
      default: false
    },
    isImpersonating: {
      type: Boolean,
      default: false
    },
    websitesModalAlertLink: {
      type: String,
      required: true
    },
    selectiveUrlSupportLink: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      color: '',
      faviconB64: '',
      showFaviconModal: false,
      isEditingColor: false,
      swatchColors: [],
      isShowingTrackEventSecret: false,
      cookieDomainValidationRules: [{
        validator: validateCookieDomain,
        trigger: ['blur']
      }],
      maxCookieTTLValidationRules: [{
        validator: validateCookieTTL,
        trigger: ['blur']
      }]
    };
  },
  computed: {
    platformLabel() {
      return upperFirst(this.app.platform);
    },
    isExtensionApp() {
      return this.app.platform === 'extension';
    },
    isWebApp() {
      return this.app.platform === 'web';
    },
    showExtensionPlatformType() {
      return this.isExtensionApp;
    },
    showExtensionDomainPatterns() {
      return this.isExtensionApp;
    },
    showSelectiveUrls() {
      return this.isExtensionApp;
    },
    showEnableShadowRoot() {
      return this.isExtensionApp;
    },
    showVisitorIdentificationFunctionSetting() {
      return this.subscriptionUsesBrowserIngest && this.isExtensionApp;
    },
    showApiKey() {
      if (this.showSetting(['adopt'], ['web'])) {
        return true;
      }
      return this.showSetting(['engage'], ['extension', 'web']) || this.isSuperUser && !this.isPartnerAdoptApplication;
    },
    showTrackEventSecretSetting() {
      return this.showSetting(['engage'], ['web'], 'pendoCore');
    },
    showCnameSetting() {
      return this.showSetting(['adopt', 'engage'], ['web']);
    },
    showFaviconSettings() {
      return this.showSetting(['adopt'], ['extension', 'web']) || this.showSetting(['engage'], ['extension', 'web'], 'pendoFavicons');
    },
    showSuggestedPagesSetting() {
      return this.showSetting(['adopt', 'engage'], ['extension', 'web']);
    },
    showTextCaptureFromEventsSetting() {
      return this.showSetting(['adopt', 'engage'], ['extension']);
    },
    showDisableInheritedPagesFeaturesSetting() {
      return this.extensionPlatformType !== 'Other' && this.showSetting(['adopt', 'engage'], ['extension']);
    },
    canEditExtensionRegularExpressions() {
      return this.isSuperUser && this.showSetting(['engage'], ['extension']);
    },
    showSiblingSelectorsSetting() {
      return this.showSetting(['adopt', 'engage'], ['extension']);
    },
    showCookiesAndDataPrivacySettings() {
      return this.showSetting(['adopt', 'engage'], ['web']);
    },
    showMaxCookieTTLSetting() {
      return this.showSetting(['engage'], ['web']);
    },
    showPreventCookieRefresh() {
      return this.showSetting(['engage'], ['web']);
    },
    showCookieDomainSetting() {
      return this.showSetting(['engage'], ['web']);
    },
    showCrossAppAgentCookiesSetting() {
      return this.showSetting(['engage'], ['web']);
    },
    showUsePostOnlySetting() {
      return this.showSetting(['adopt', 'engage'], ['web']);
    },
    showNoCookiesNoLocalstorageSetting() {
      return this.showSetting(['adopt', 'engage'], ['web']);
    },
    showDisableAgentIdentificationPersistenceSetting() {
      return this.showSetting(['adopt', 'engage'], ['web']);
    },
    showDisableClientSideStorageSetting() {
      return this.showSetting(['adopt', 'engage'], ['web']);
    },
    showGuidesToAnonymousVisitorsSetting() {
      return this.showSetting(['adopt', 'engage'], ['extension', 'web'], 'pendoCore');
    },
    showTrackEventTimeOnSiteSetting() {
      return this.showSetting(['engage'], ['web']);
    },
    showDisableDesignerLaunchUrlToken() {
      return this.showSetting(['adopt'], ['extension']);
    },
    showDisableFeedbackAutoInit() {
      return this.showSetting(['engage'], ['web'], 'pendoFeedback');
    },
    showDisableGlobalCss() {
      return this.showSetting(['engage'], ['web']);
    },
    showMutationObserverSetting() {
      return this.showSetting(['adopt', 'engage'], ['web']);
    },
    showShadowDomMutationObserverSetting() {
      return this.app.applicationFlags.preferMutationObserver && this.showSetting(['adopt', 'engage'], ['web']);
    },
    showPreferBroadcastChannelSetting() {
      return this.isWebApp;
    },
    disableSharedCookies() {
      return this.app.applicationFlags.disableCookies;
    },
    extensionPlatformType() {
      return this.app.extensionPlatformTypeDisplayName || 'Other';
    },
    pageEventPropertiesButtonValue() {
      return this.app.applicationFlags.pageBasedEventProperties;
    },
    collectPageTitlesButtonValue() {
      return this.app.applicationFlags.pageBasedEventProperties && get(this.app, 'eventPropertyConfigurations', []).some(ep => ep.name === 'document_title');
    },
    subscriptionUsesBrowserIngest() {
      return get(this.subscription, 'featureFlags.enableExtensionBrowserIngest', false);
    },
    isCnameConfigured() {
      return !!(this.app.applicationFlags.cname && this.app.contentCname && this.app.dataCname);
    },
    brandingText() {
      return this.isEngage ? 'Make it easy to identify apps by adding a favicon.' : 'Make it easy to identify apps by adding a favicon. Assigned colors are used in some charts.';
    },
    platformIcon() {
      return {
        type: this.isEngage && this.app.platform === 'web' ? 'monitor' : 'puzzle-piece',
        fill: this.isEngage ? '#FFFFFF' : this.color
      };
    },
    maxCookieTTLDays() {
      return this.app.maxCookieTTLDays === 0 ? '' : this.app.maxCookieTTLDays;
    },
    isPartnerAdoptApplication() {
      return get(this.app, 'applicationFlags.partnerAdoptApplication', false);
    }
  },
  created() {
    if (this.app) {
      this.faviconB64 = get(this.app, 'faviconB64', '');
      this.color = get(this.app, 'color', '');
    }
  },
  methods: {
    onDisplayNameChange(value) {
      if (value !== this.app.displayName) {
        this.$emit('update-app-name', {
          app: this.app,
          updates: {
            displayName: value
          }
        });
      }
    },
    onMaxCookieTTLChange() {
      let value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      if (value === '') {
        value = 0;
      }
      if (value !== this.app.maxCookieTTLDays) {
        this.$emit('update-app', {
          app: this.app,
          updates: {
            maxCookieTTLDays: value
          }
        });
      }
    },
    onCookieDomainChange() {
      let value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
      if (value !== this.app.cookieDomain) {
        this.$emit('update-app', {
          app: this.app,
          updates: {
            cookieDomain: value
          }
        });
      }
    },
    onSharedCookieChange() {
      let sharedCookieKey = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
      this.$emit('update-app', {
        app: this.app,
        updates: {
          identityStorageSuffix: sharedCookieKey.length ? '' : `${this.subscription.hash}`
        }
      });
    },
    handleFlagChange(enabled, flag) {
      this.$emit('update-app-flag', {
        appId: this.app.id,
        flag,
        enabled
      });
    },
    configurePageEventProperties(enabled) {
      if (enabled === this.pageEventPropertiesButtonValue) {
        return;
      }
      this.$emit('update-app', {
        app: this.app,
        updates: {
          applicationFlags: {
            pageBasedEventProperties: enabled
          }
        }
      });
    },
    configureCollectPageTitles(enabled, name, isActive, path) {
      if (enabled === this.collectPageTitlesButtonValue) {
        return;
      }
      const eventPropertyConfigurations = get(this.app, 'eventPropertyConfigurations', []).filter(config => config.name !== name);
      if (enabled) {
        eventPropertyConfigurations.push({
          name,
          isActive,
          path
        });
      }
      this.$emit('update-app', {
        app: this.app,
        updates: {
          eventPropertyConfigurations
        }
      });
    },
    onSuggestPageTagsChange(event) {
      this.$emit('update-suggest-page-tags', {
        appId: this.app.id,
        flag: 'suggestPageTags',
        enabled: event
      });
    },
    getColors: _async(function (favicon) {
      const _this = this;
      return _invokeIgnored(function () {
        if (favicon) {
          return _awaitIgnored(Vibrant.from(favicon).getPalette((err, palette) => {
            if (palette.Vibrant) {
              _this.color = palette.Vibrant.hex;
              _this.swatchColors = uniq([palette.Vibrant.hex, palette.DarkMuted.hex, palette.DarkVibrant.hex, palette.LightMuted.hex, palette.LightVibrant.hex, palette.Muted.hex]);
            }
          }));
        } else {
          _this.color = '';
          _this.swatchColors = [];
        }
      });
    }),
    onColorChange(newColor) {
      this.isEditingColor = false;
      this.color = newColor;
      if (this.color !== '' && this.color !== null) {
        this.$emit('update-app', {
          app: this.app,
          updates: {
            color: this.color
          }
        });
      }
    },
    displayFaviconModal() {
      this.showFaviconModal = true;
    },
    getFaviconByUrl(url) {
      this.$emit('fetch-url', url);
    },
    closeFaviconModal() {
      this.showFaviconModal = false;
    },
    submitFavicon(event) {
      this.faviconB64 = event;
      this.showFaviconModal = false;
      if (!this.isEngage) {
        this.getColors(this.faviconB64);
      }
      this.$emit('update-app', {
        app: this.app,
        updates: {
          faviconB64: this.faviconB64
        }
      });
    },
    showSetting(intendedUIs, platforms) {
      let engageRequirments = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
      if (!this.app) {
        return false;
      }
      if (this.isPartnerAdoptApplication) {
        return false;
      }
      if (this.consumingPlatform === 'engage' && engageRequirments !== '') {
        if (engageRequirments === 'pendoCore') {
          return platforms.includes(get(this.app, 'platform', '')) && intendedUIs.includes(this.consumingPlatform) && this.engageCoreEnabled;
        }
        if (engageRequirments === 'pendoFeedback') {
          return platforms.includes(get(this.app, 'platform', '')) && intendedUIs.includes(this.consumingPlatform) && this.engageFeedbackEnabled;
        }
        if (engageRequirments === 'pendoFavicons') {
          return platforms.includes(get(this.app, 'platform', '')) && intendedUIs.includes(this.consumingPlatform) && this.isEngage;
        }
      }
      return platforms.includes(get(this.app, 'platform', '')) && intendedUIs.includes(this.consumingPlatform);
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-app-details.vue2.js.map
